import React, { useEffect, useState } from "react";
import { FileText } from "lucide-react";
import { awaitFirstPost } from "@/lib/resumeIntake";
import { trackEvent } from "@/lib/tracker";
import TypedPostCard from "@/components/postings/TypedPostCard";

/* Their post, on the signup screen, before they have an account.

   Resume uploaders used to go straight from the file picker to the account
   form with nothing to show for it: the posts were written in the background
   and first seen after login. Half the people who reached the composer left
   between seeing it and getting anything back, so the thing that was supposed
   to sell the account was arriving after the account.

   The first post now comes back in about four seconds (resumeIntake asks for
   it on its own). Until it does, the card types their name from the quick
   read; then the post's city and the post itself. See TypedPostCard.

   If the first post fails, the card is replaced by the plain "we're reading
   it" note this screen had before, and signup carries on exactly as it did. */
export default function ResumePostPreview({ name = "", fileName = "" }) {
  // undefined while waiting, null when it failed, the posting when it landed.
  const [post, setPost] = useState(undefined);

  useEffect(() => {
    let alive = true;
    const startedAt = Date.now();
    awaitFirstPost().then((result) => {
      if (!alive) return;
      const first = result?.available ? result.postings?.[0] : null;
      setPost(first || null);
      trackEvent("resume_preview_shown", {
        flow: "need_first", ok: !!first, wait_ms: Date.now() - startedAt,
      });
    });
    return () => { alive = false; };
  }, []);

  if (post === null) {
    return (
      <div className="mt-4 flex items-start gap-3 rounded-xl border border-line bg-brand-tint p-3">
        <span className="h-8 w-8 rounded-lg bg-surface text-brand grid place-items-center flex-shrink-0">
          <FileText size={16} strokeWidth={1.8} aria-hidden="true" />
        </span>
        <span className="min-w-0">
          <span className="block text-ink text-sm font-medium min-w-0 break-words">{fileName}</span>
          <span className="block text-secondary text-xs mt-0.5 leading-relaxed">
            We're reading it now and pre-filling your profile. Your posts will be waiting when you finish.
          </span>
        </span>
      </div>
    );
  }

  return (
    <div className="mt-4">
      <p className="text-xs font-semibold text-ink mb-2">Here's what it's going to look like</p>
      {/* The post's own location, not the quick read's. The quick read is a
          regex over the top of the page and can take an employer line for a
          city ("Health Physicians- Indianapolis, IN"); the model reads the
          whole resume. The card shows the name alone until the post lands. */}
      <TypedPostCard name={name} location={post?.location || ""} post={post} />
      <p className="mt-2 text-xs text-tertiary">
        Save your progress by signing up. You'll review your posts before anything goes live.
      </p>
    </div>
  );
}
